import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, LineChart, Line, ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, Wallet, DollarSign, PiggyBank, Banknote } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { MoneyTransaction } from '../types';
import { formatVND, formatVNDAxis } from '../utils/format';
import {
  buildCapitalFlowSeries, buildCashFlowRealSeries,
  getTotalNAV, CASH_WALLET_ASSET_ID,
} from '../utils/calculations';
import { SensitiveValue } from './ui/SensitiveValue';

// ── Tooltip dùng chung ────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: 'var(--tx-3)', marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.fill || p.color, fontWeight: 500, marginBottom: 2 }}>
          {p.name}: {formatVND(p.value)}
        </div>
      ))}
    </div>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon: Icon }: {
  label: string; value: number; sub?: string; color: string;
  icon: React.FC<{ size?: number; color?: string; strokeWidth?: number }>;
}) {
  return (
    <div style={{ flex: 1, minWidth: 150, padding: '14px 16px', borderRadius: 14, background: 'var(--bg-card)', border: `1px solid ${color}22` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon size={14} color={color} strokeWidth={2} />
        <span style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <SensitiveValue>
        <div style={{ fontSize: 20, fontWeight: 700, color }}>{formatVND(value)}</div>
      </SensitiveValue>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function MoneyFlowReports() {
  const { state } = useAppState();
  const { moneyTransactions = [], assets = [] } = state;
  const txs = moneyTransactions as MoneyTransaction[];
  const walletAsset = (assets as any[]).find(a => a.id === CASH_WALLET_ASSET_ID);

  const [activeTab, setActiveTab] = useState<'capital' | 'cashflow'>('capital');
  const [months, setMonths]       = useState(12);

  // ── KPIs tổng quan ───────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const nav         = getTotalNAV(assets as any[]);
    const totalIn     = txs.filter(t => t.type === 'DEPOSIT').reduce((s, t) => s + t.net_amount, 0);
    const totalOut    = txs.filter(t => t.type === 'WITHDRAW').reduce((s, t) => s + t.net_amount, 0);
    const netCapital  = totalIn - totalOut;
    const organicGain = nav - netCapital;
    const totalYield  = txs.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.net_amount, 0);
    const totalSell   = txs.filter(t => t.type === 'SELL').reduce((s, t) => s + t.net_amount, 0);
    return { nav, netCapital, organicGain, totalYield, totalSell };
  }, [txs, assets]);

  // ── Global wallet balance — lấy từ tài sản đặc biệt ─────────────────────
  const totalWallet = walletAsset?.currentPrice ?? 0;

  // ── Chart data ───────────────────────────────────────────────────────────
  const capitalData  = useMemo(() => buildCapitalFlowSeries(txs, months), [txs, months]);
  const cashFlowData = useMemo(() => buildCashFlowRealSeries(txs, months), [txs, months]);

  // ── Cumulative net capital ────────────────────────────────────────────────
  const cumulativeData = useMemo(() => {
    let cum = 0;
    return capitalData.map(d => {
      cum += d.vonNap - d.rutVon;
      return { ...d, cumVon: cum };
    });
  }, [capitalData]);

  const tabs = [
    { key: 'capital',   label: 'Biến động vốn' },
    { key: 'cashflow',  label: 'Dòng tiền thực' },
  ] as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header KPIs ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
          Tổng quan dòng vốn
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <KpiCard label="Vốn đã nạp ròng"    value={kpi.netCapital}  color="#fbbf24" icon={PiggyBank}   sub="DEPOSIT − WITHDRAW tích luỹ" />
          <KpiCard label="Giá trị tài sản"     value={kpi.nav}         color="#38bdf8" icon={TrendingUp}  sub="Tổng NAV hiện tại" />
          <KpiCard label="Tăng trưởng tự thân" value={kpi.organicGain} color={kpi.organicGain >= 0 ? '#10b981' : '#f87171'} icon={kpi.organicGain >= 0 ? TrendingUp : TrendingDown} sub="NAV − Vốn nạp ròng" />
          <KpiCard label="Yield tích luỹ"      value={kpi.totalYield}  color="#a3e635" icon={DollarSign}  sub="Cổ tức, lãi, tiền thuê" />
        </div>
      </div>

      {/* ── Ví chung toàn hệ thống ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Ví tiền mặt khả dụng
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderRadius: 14, background: 'var(--bg-card)', border: `1px solid ${totalWallet >= 0 ? 'rgba(167,139,250,0.3)' : 'rgba(248,113,113,0.3)'}` }}>
          <Wallet size={18} color={totalWallet >= 0 ? '#a78bfa' : '#f87171'} strokeWidth={1.5} />
          <div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4 }}>🪙 Ví chung (Global Wallet)</div>
            <SensitiveValue>
              <div style={{ fontSize: 22, fontWeight: 700, color: totalWallet >= 0 ? '#a78bfa' : '#f87171', lineHeight: 1 }}>
                {formatVND(totalWallet)}
              </div>
            </SensitiveValue>
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>
              Tích từ DEPOSIT + SELL + INCOME − BUY − WITHDRAW
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs + chart ── */}
      <div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                  background: activeTab === t.key ? 'rgba(167,139,250,0.15)' : 'var(--bg-card-el)',
                  border: `1px solid ${activeTab === t.key ? 'rgba(167,139,250,0.4)' : 'var(--bd)'}`,
                  color: activeTab === t.key ? '#a78bfa' : 'var(--tx-3)' }}>
                {t.label}
              </button>
            ))}
          </div>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-3)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value={6}>6 tháng</option>
            <option value={12}>12 tháng</option>
            <option value={24}>24 tháng</option>
          </select>
        </div>

        {/* Report 1: Biến động vốn */}
        {activeTab === 'capital' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', padding: '8px 12px', borderRadius: 8, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}>
              <b style={{ color: '#fbbf24' }}>Nạp thêm vốn</b> = DEPOSIT − WITHDRAW mỗi tháng &nbsp;·&nbsp;
              <b style={{ color: '#38bdf8' }}>Triển khai</b> = BUY vào tài sản &nbsp;·&nbsp;
              <b style={{ color: '#10b981' }}>Thu về ví</b> = SELL + INCOME
            </div>

            {/* Stacked bar: vonNap vs trienkhai vs thuVe */}
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={cumulativeData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatVNDAxis} tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="vonNap"    name="Nạp vốn"      fill="#fbbf24" radius={[3,3,0,0]} maxBarSize={28} />
                <Bar dataKey="trienkhai" name="Triển khai"   fill="#38bdf8" radius={[3,3,0,0]} maxBarSize={28} />
                <Bar dataKey="thuVe"     name="Thu về ví"    fill="#10b981" radius={[3,3,0,0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>

            {/* Line: cumulative capital injected */}
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginBottom: 4 }}>Vốn nạp ròng tích luỹ (DEPOSIT − WITHDRAW)</div>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={cumulativeData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatVNDAxis} tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Line dataKey="cumVon" name="Vốn ròng tích luỹ" stroke="#fbbf24" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Report 2: Dòng tiền thực */}
        {activeTab === 'cashflow' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', padding: '8px 12px', borderRadius: 8, background: 'rgba(163,230,53,0.06)', border: '1px solid rgba(163,230,53,0.15)' }}>
              <b style={{ color: '#a3e635' }}>Yield</b> = Thu nhập thực từ tài sản (cổ tức, lãi, tiền thuê) &nbsp;·&nbsp;
              <b style={{ color: '#38bdf8' }}>Bán tài sản</b> = Tiền về ví khi SELL &nbsp;·&nbsp;
              <b style={{ color: '#fbbf24' }}>Vốn ngoài</b> = Tiền nạp từ lương/thưởng
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={cashFlowData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatVNDAxis} tick={{ fontSize: 10, fill: 'var(--tx-5)' }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="var(--bd)" />
                <Bar dataKey="vonNgoai" name="Vốn bên ngoài" fill="#fbbf24" radius={[3,3,0,0]} maxBarSize={22} />
                <Bar dataKey="yield_"   name="Yield tài sản" fill="#a3e635"  radius={[3,3,0,0]} maxBarSize={22} />
                <Bar dataKey="realized" name="Bán tài sản"   fill="#38bdf8"  radius={[3,3,0,0]} maxBarSize={22} />
                <Bar dataKey="rutRa"    name="Rút vốn"       fill="#f87171"  radius={[3,3,0,0]} maxBarSize={22} stackId="out" />
              </BarChart>
            </ResponsiveContainer>

            {/* Summary table */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Tổng yield tích luỹ', value: kpi.totalYield, color: '#a3e635', sub: 'Cổ tức, lãi, tiền thuê' },
                { label: 'Tổng bán tài sản',    value: kpi.totalSell,  color: '#38bdf8', sub: 'Tiền về ví từ SELL' },
              ].map(c => (
                <div key={c.label} style={{ flex: 1, minWidth: 160, padding: '12px 16px', borderRadius: 12, background: 'var(--bg-card)', border: `1px solid ${c.color}22` }}>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 6 }}>{c.label}</div>
                  <SensitiveValue>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{formatVND(c.value)}</div>
                  </SensitiveValue>
                  <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 3 }}>{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
